import type { Rng } from '../../math/rng.ts';
import type { ProblemTemplate } from '../../types.ts';
import {
  addWithoutCarrying,
  columnSlip,
  countingRun,
  lerp,
  scaled,
  sequencePrompt,
  smallerFromLarger,
  wholeProblem,
} from './helpers-g01-04.ts';

/**
 * Grade 3 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 *
 * CAPS Grade 3: the 2, 3, 4, 5 and 10 times tables (products ≤ 100) with
 * multiplication and division as inverses, number sentences with □, number
 * patterns (3s, 4s, 5s, 25s, 50s to 1000) and adding/subtracting to 100.
 */

/** A Grade 3 table. The 2, 5 and 10 tables come first; 3 and 4 join as difficulty rises. */
function pickTable(rng: Rng, difficulty: number): number {
  return rng.chance(lerp(0, 0.6, difficulty)) ? rng.pick([3, 4]) : rng.pick([2, 5, 10]);
}

/** The other factor of a table fact: 2–5 at difficulty 0, 3–10 at difficulty 1 (the ×2 facts fade out). */
function pickFactor(rng: Rng, difficulty: number, except: readonly number[] = []): number {
  return rng.intExcept(scaled(2, 3, difficulty), scaled(5, 10, difficulty), except);
}

const multMissing: ProblemTemplate = {
  id: 'g3.mult-missing',
  grade: 3,
  title: 'Missing factor: □ × b = c (tables 2, 3, 4, 5, 10)',
  parTimeMs: 8000,
  generate(rng, difficulty) {
    const table = pickTable(rng, difficulty);
    const answer = pickFactor(rng, difficulty, [table]); // 4 × 4 would show the answer in the prompt
    const product = table * answer;
    const prompt = rng.chance(lerp(0, 0.5, difficulty))
      ? `${table} \\times \\box = ${product}`
      : `\\box \\times ${table} = ${product}`;
    return wholeProblem(prompt, answer, [
      product - table, // treated × like + and subtracted
      answer + 1, // slipped to the neighbouring fact in the table
      answer - 1, // slipped to the neighbouring fact in the table
    ]);
  },
};

const divMissing: ProblemTemplate = {
  id: 'g3.div-missing',
  grade: 3,
  title: 'Missing divisor: c ÷ □ = b',
  parTimeMs: 9000,
  generate(rng, difficulty) {
    const table = pickTable(rng, difficulty);
    const factor = pickFactor(rng, difficulty, [table]);
    const [divisor, quotient] = rng.chance(0.5) ? [table, factor] : [factor, table];
    const dividend = table * factor;
    return wholeProblem(`${dividend} \\div \\box = ${quotient}`, divisor, [
      dividend - quotient, // treated ÷ like − (wrong operation)
      divisor + 1, // slipped to the neighbouring fact in the table
      divisor - 1, // slipped to the neighbouring fact in the table
    ]);
  },
};

const balanceMixed: ProblemTemplate = {
  id: 'g3.balance-mixed',
  grade: 3,
  title: 'Balance both sides: a × b = □ + c',
  parTimeMs: 11000,
  generate(rng, difficulty) {
    const table = pickTable(rng, difficulty);
    const factor = pickFactor(rng, difficulty);
    const product = table * factor;
    // Keep the answer different from every number shown, so it can't be copied.
    const maxAddend = Math.min(product - 1, scaled(5, 60, difficulty));
    const halfProduct = product % 2 === 0 ? [product / 2] : [];
    const addend = rng.intExcept(1, maxAddend, [product - table, product - factor, ...halfProduct]);
    const answer = product - addend;
    const left = rng.chance(0.5) ? `${table} \\times ${factor}` : `${factor} \\times ${table}`;
    const right = rng.chance(lerp(0, 0.5, difficulty)) ? `${addend} + \\box` : `\\box + ${addend}`;
    return wholeProblem(`${left} = ${right}`, answer, [
      product, // "=" read as "write the answer next": just multiplied
      product + addend, // added the addend instead of taking it away
      table + factor - addend, // added a + b instead of multiplying
      answer + 1, // off by one
    ]);
  },
};

/** Steps a learner may confuse with the real one (e.g. counting in 20s instead of 25s). */
const CONFUSED_STEPS: Record<number, readonly [number, number]> = {
  3: [2, 4],
  4: [3, 5],
  5: [4, 10],
  25: [20, 50],
  50: [25, 100],
};

const pattern: ProblemTemplate = {
  id: 'g3.pattern',
  grade: 3,
  title: 'Number patterns in 3s, 4s, 5s, 25s and 50s',
  parTimeMs: 9000,
  generate(rng, difficulty) {
    const big = rng.chance(lerp(0.2, 0.5, difficulty));
    const step = big ? rng.pick([25, 50]) : rng.pick([3, 4, 5]);
    const limit = Math.max(big ? scaled(200, 1000, difficulty) : scaled(30, 100, difficulty), 5 * step);
    // Off the multiples at higher difficulty ("7, 11, 15"); big steps shift by a multiple of 5 ("10, 35, 60").
    let offset = 0;
    if (rng.chance(lerp(0, 0.7, difficulty))) offset = big ? 5 * rng.int(1, step / 5 - 1) : rng.int(1, step - 1);
    const run = countingRun(rng, step, 4, limit, offset);
    const backward = rng.chance(lerp(0, 0.35, difficulty));
    const ordered = backward ? run.slice().reverse() : run;
    const dir = backward ? -1 : 1;
    const last = ordered[2]!;
    const answer = ordered[3]!;
    const [wrongA, wrongB] = CONFUSED_STEPS[step]!;
    return wholeProblem(
      sequencePrompt(ordered.slice(0, 3)),
      answer,
      [
        last + dir * wrongA, // used a neighbouring step size
        last + dir * wrongB, // used a neighbouring step size
        answer + dir * step, // skipped a term
        columnSlip(last, step, backward), // forgot to carry / didn't borrow in the column
        ...(backward ? [last + step] : []), // assumed the pattern goes up
      ],
      { key: 'next' },
    );
  },
};

const addSub100: ProblemTemplate = {
  id: 'g3.add-sub-100',
  grade: 3,
  title: 'Missing number to 100: □ − b = c (and □ + b = c)',
  parTimeMs: 11000,
  generate(rng, difficulty) {
    // Build two 2-digit numbers column by column so regrouping (carry/borrow) is a deliberate difficulty choice.
    const regroup = rng.chance(lerp(0, 0.8, difficulty));
    const xOnes = regroup ? rng.int(1, 9) : rng.int(1, 8);
    const yOnes = regroup ? rng.int(10 - xOnes, 9) : rng.int(1, 9 - xOnes);
    const carry = regroup ? 1 : 0;
    const maxTens = scaled(5, 9, difficulty) - carry; // the sum stays ≤ 99 (≤ 59 at difficulty 0)
    const xTens = rng.int(1, maxTens - 1);
    const yTens = rng.int(1, maxTens - xTens);
    const x = 10 * xTens + xOnes;
    const y = 10 * yTens + yOnes;
    const sum = x + y;
    // Mostly the missing start of a subtraction (catalogue form); sometimes the addition twin.
    const wantAddition = rng.chance(0.3);
    if (wantAddition && x !== y) {
      return wholeProblem(`\\box + ${x} = ${sum}`, y, [
        sum + x, // added instead of subtracting (wrong operation)
        smallerFromLarger(sum, x), // didn't borrow: took smaller digit from larger in each column
        y + 10, // borrowed a ten but forgot to reduce the tens
        y - 10, // miscounted the tens
      ]);
    }
    return wholeProblem(`\\box - ${x} = ${y}`, sum, [
      Math.abs(x - y), // subtracted the numbers it can see (took the minus sign literally)
      addWithoutCarrying(x, y), // added but forgot to carry the ten
      sum + 10, // miscounted the tens
      sum - 10, // miscounted the tens
    ]);
  },
};

export const GRADE_03: ProblemTemplate[] = [multMissing, divMissing, balanceMixed, pattern, addSub100];
